import { TransactionDirection, type FinanceTransactionRecord } from "@/features/finance/model/finance-types"
import { getBranchDisplayLabel } from "@/shared/lib/branch/branch-display"

type Translate = (key: string, values?: Record<string, string | number>) => string

/**
 * How a transaction reads to a person, in one line: its account (or description), and for a transfer the branch
 * on the other side. A TRANSFER IS NOT AN EXPENSE: it moves money through Transfer Clearing rather than
 * spending it, so it is never labelled "Expense". `tOverview` is the `finance.overview` translator.
 */
export function describeTransaction(
  record: Pick<FinanceTransactionRecord, "type" | "transfer" | "accountingAccount" | "description">,
  tOverview: Translate,
): { label: string; isIncome: boolean; isTransfer: boolean } {
  const isIncome = record.type === TransactionDirection.Income || record.type === TransactionDirection.TransferIn
  const isTransfer =
    record.type === TransactionDirection.TransferIn ||
    record.type === TransactionDirection.TransferOut ||
    record.type === TransactionDirection.Transfer

  if (isTransfer) {
    const counterpart = record.transfer?.counterpartBranch
    const label = counterpart
      ? tOverview(
          record.type === TransactionDirection.TransferIn
            ? "widgets.recentTransactions.transferFrom"
            : "widgets.recentTransactions.transferTo",
          {
            branch: getBranchDisplayLabel({
              branchType: counterpart.branchType,
              branchName: counterpart.name,
              parishLabel: tOverview("widgets.recentTransactions.parish"),
              fallbackLabel: counterpart.name,
            }),
          },
        )
      : tOverview("widgets.recentTransactions.transfer")
    return { label, isIncome, isTransfer }
  }

  const label = record.accountingAccount
    ? record.accountingAccount.name
    : record.description ??
      (isIncome ? tOverview("widgets.recentTransactions.income") : tOverview("widgets.recentTransactions.expense"))
  return { label, isIncome, isTransfer }
}
